import React, { useMemo } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { FORECAST_90_DAY_ENDPOINT } from "@/data/demo-data";
import {
  BANK_ACCOUNTS,
  MONTHLY_OPERATING_EXPENSES,
  MONTHLY_REVENUE,
} from "@/data/financial-systems";
import { useBaseForecast } from "@/hooks/useBaseForecast";
import { useFormatting } from "@/hooks/useFormatting";
import { useTerminology } from "@/hooks/useTerminology";

const MONTHLY_EXPENSES = MONTHLY_OPERATING_EXPENSES;
const CURRENT_CASH = BANK_ACCOUNTS.reduce((sum, a) => sum + a.balance, 0);

export const ForecastSummaryPreview: React.FC = () => {
  const { formatCurrency } = useFormatting();
  const { getCashDurationTerm, getCashMovementTerms, getForecastTerm } =
    useTerminology();
  const abbreviate = (v: number) =>
    v >= 1_000_000
      ? `£${(v / 1_000_000).toFixed(1)}M`
      : v >= 1_000
        ? `£${(v / 1_000).toFixed(0)}k`
        : `£${v}`;
  const terms = getCashMovementTerms();
  const forecastTerm = getForecastTerm(true);

  const { baseForecast: forecastData } = useBaseForecast();

  // Thin out to weekly data points for the chart
  const chartData = useMemo(
    () =>
      forecastData
        .filter((_, i) => i % 7 === 0 || i === 90)
        .map((d) => ({
          date: d.date.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
          }),
          balance: Math.round(d.balance),
        })),
    [forecastData],
  );

  // 12-month outlook (extrapolated from 90-day trend)
  const day90Balance = FORECAST_90_DAY_ENDPOINT;
  const dailyBurn = (CURRENT_CASH - day90Balance) / 90;
  const month12Balance = Math.round(day90Balance - dailyBurn * 275); // remaining ~275 days

  // 3-year headline (budget-based recovery assumption)
  const year3Balance = 380_000; // strategic budget target

  const kpis = [
    {
      label: "90-Day Closing Balance",
      value: formatCurrency(day90Balance),
      change: `${formatCurrency(CURRENT_CASH - day90Balance)} decline`,
      positive: false,
    },
    {
      label: "12-Month Projected Balance",
      value: formatCurrency(Math.max(month12Balance, 45_000)),
      change: `At current burn rate`,
      positive: month12Balance > 100_000,
    },
    {
      label: "3-Year Target Balance",
      value: formatCurrency(year3Balance),
      change: "Strategic budget target",
      positive: true,
    },
    {
      label: `Monthly ${terms.in}`,
      value: formatCurrency(MONTHLY_REVENUE),
      change: "Average (FY2025/26)",
      positive: true,
    },
    {
      label: "Monthly Operating Costs",
      value: formatCurrency(MONTHLY_EXPENSES),
      change: "Fixed + variable",
      positive: null,
    },
    {
      label: getCashDurationTerm(3.9),
      value: "3.9 months",
      change: "Of operating costs covered",
      positive: false,
    },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="rounded-lg border bg-muted/40 p-4">
            <p className="text-xs text-muted-foreground mb-1">{kpi.label}</p>
            <p className="text-lg font-bold">{kpi.value}</p>
            <p
              className={`text-xs mt-1 ${kpi.positive === true ? "text-green-600" : kpi.positive === false ? "text-amber-600" : "text-muted-foreground"}`}
            >
              {kpi.change}
            </p>
          </div>
        ))}
      </div>

      {/* 90-day trend chart */}
      <div>
        <p className="text-sm font-semibold mb-3">90-Day Cash Balance Trend</p>
        <div className="h-52 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                interval={1}
                className="fill-muted-foreground"
              />
              <YAxis
                tickFormatter={(v) => abbreviate(v)}
                tick={{ fontSize: 11 }}
                className="fill-muted-foreground"
              />
              <Tooltip
                formatter={
                  ((value: number) => [
                    formatCurrency(value),
                    "Balance",
                  ]) as never
                }
                labelStyle={{ fontSize: 12 }}
                contentStyle={{ fontSize: 12 }}
              />
              <ReferenceLine
                y={50_000}
                stroke="#ef4444"
                strokeDasharray="4 4"
                label={{
                  value: "Min threshold",
                  fontSize: 10,
                  fill: "#ef4444",
                }}
              />
              <Line
                type="monotone"
                dataKey="balance"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Horizon summary */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/60">
              <th className="px-4 py-3 text-left font-semibold">
                {forecastTerm} Horizon
              </th>
              <th className="px-4 py-3 text-right font-semibold">
                Opening Balance
              </th>
              <th className="px-4 py-3 text-right font-semibold">
                Projected Closing
              </th>
              <th className="px-4 py-3 text-right font-semibold">Movement</th>
            </tr>
          </thead>
          <tbody>
            {[
              { horizon: "90-Day", open: CURRENT_CASH, close: day90Balance },
              {
                horizon: "12-Month",
                open: CURRENT_CASH,
                close: Math.max(month12Balance, 45_000),
              },
              { horizon: "3-Year", open: CURRENT_CASH, close: year3Balance },
            ].map((row) => {
              const movement = row.close - row.open;
              return (
                <tr
                  key={row.horizon}
                  className="border-b hover:bg-muted/30 transition-colors"
                >
                  <td className="px-4 py-2.5 font-medium">{row.horizon}</td>
                  <td className="px-4 py-2.5 text-right">
                    {formatCurrency(row.open)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium">
                    {formatCurrency(row.close)}
                  </td>
                  <td
                    className={`px-4 py-2.5 text-right ${movement >= 0 ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"}`}
                  >
                    {movement >= 0 ? "+" : ""}
                    {formatCurrency(movement)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
